import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import {
  InvoiceStatusBadge,
  type InvoiceStatus,
} from '@/client/components/invoices/InvoiceStatusBadge';
import { PageHeader } from '@/client/components/common/PageHeader';
import { Button } from '@/client/components/ui/button';
import { Card } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { fmtAmount, type Currency } from '@/client/lib/currency';
import { trpc } from '@/client/utils/trpc';

const searchSchema = z.object({
  status: z.enum(['ALL', 'SENT', 'PAID', 'OVERDUE', 'DRAFT']).optional().default('ALL'),
});

export const Route = createFileRoute('/_app/invoices/')({
  validateSearch: (s: Record<string, unknown>) => searchSchema.parse(s),
  component: InvoicesListPage,
});

function fmtEur(n: number): string {
  return `€${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)}`;
}


function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

type StatusFilter = 'ALL' | 'SENT' | 'PAID' | 'OVERDUE' | 'DRAFT';

const STATUS_TABS: {
  value: StatusFilter;
  label: (kpis: { drafts: number; sentCount: number }) => string;
}[] = [
  { value: 'ALL', label: () => 'All' },
  { value: 'SENT', label: () => 'Outstanding' },
  { value: 'PAID', label: () => 'Paid' },
  { value: 'OVERDUE', label: () => 'Overdue' },
  { value: 'DRAFT', label: ({ drafts }) => (drafts > 0 ? `Drafts · ${drafts}` : 'Drafts') },
];

function KpiCard({
  label,
  value,
  sub,
  valueClass,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
}) {
  return (
    <Card className="p-4">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${valueClass ?? ''}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

function InvoicesListPage() {
  useAdminGuard();

  const search = Route.useSearch();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const { data, isLoading } = trpc.invoices.list.useQuery();
  const invoices = data?.invoices ?? [];
  const kpis = data?.kpis ?? {
    outstanding: 0,
    paidThisMonth: 0,
    overdue: 0,
    drafts: 0,
    sentCount: 0,
  };

  const filtered = useMemo(() => {
    let result = invoices;
    if (search.status !== 'ALL') result = result.filter((i) => i.status === search.status);
    if (q.trim()) {
      const lower = q.toLowerCase();
      result = result.filter(
        (i) =>
          i.number.toLowerCase().includes(lower) ||
          i.client_name.toLowerCase().includes(lower) ||
          (i.project_name ?? '').toLowerCase().includes(lower),
      );
    }
    return result;
  }, [invoices, search.status, q]);

  function setStatus(s: StatusFilter) {
    navigate({ to: '/invoices', search: { status: s } });
  }

  const now = new Date();
  const monthName = now.toLocaleString('en-US', { month: 'long' });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        actions={
          <Button size="sm" asChild>
            <Link to="/invoices/new">+ New invoice</Link>
          </Button>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-4 gap-3">
        <KpiCard
          label="Outstanding"
          value={fmtEur(kpis.outstanding)}
          sub={`${kpis.sentCount} invoice${kpis.sentCount !== 1 ? 's' : ''}`}
        />
        <KpiCard
          label={`Paid · ${monthName}`}
          value={fmtEur(kpis.paidThisMonth)}
          valueClass="text-good"
        />
        <KpiCard
          label="Overdue"
          value={fmtEur(kpis.overdue)}
          valueClass={kpis.overdue > 0 ? 'text-destructive' : undefined}
        />
        <KpiCard
          label="Drafts"
          value={String(kpis.drafts)}
          sub={kpis.drafts > 0 ? 'ready to send' : 'none pending'}
        />
      </div>

      <Card className="overflow-hidden">
        {/* Tab strip + search */}
        <div className="flex items-center gap-3 border-b px-5 py-2">
          <div className="flex gap-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatus(tab.value)}
                className={[
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  search.status === tab.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {tab.label(kpis)}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Find an invoice…"
              className="pl-9 h-8 text-sm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No invoices found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3 text-left">Invoice</th>
                <th className="px-5 py-3 text-left">Client</th>
                <th className="px-5 py-3 text-left">Project</th>
                <th className="px-5 py-3 text-left">Issued</th>
                <th className="px-5 py-3 text-left">Due</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b last:border-0 hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => navigate({ to: '/invoices/$id', params: { id: inv.id } })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate({ to: '/invoices/$id', params: { id: inv.id } });
                    }
                  }}
                  tabIndex={0}
                >
                  <td className="px-5 py-3 font-mono font-semibold">{inv.number}</td>
                  <td className="px-5 py-3 font-medium">{inv.client_name}</td>
                  <td className="px-5 py-3 text-muted-foreground">{inv.project_name ?? '—'}</td>
                  <td className="px-5 py-3 tabular-nums text-muted-foreground">
                    {fmtDate(inv.issue_date)}
                  </td>
                  <td className="px-5 py-3 tabular-nums text-muted-foreground">
                    {fmtDate(inv.due_date)}
                  </td>
                  <td className="px-5 py-3">
                    <InvoiceStatusBadge status={inv.status as InvoiceStatus} />
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">
                    {fmtAmount(inv.total, inv.currency as Currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
